import { useMemo } from "react";
import { Link, Navigate, Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { AppBar } from "./Shell";
import { CheckerThumb } from "../components/primitives";
import { ApiError, getJob } from "../lib/api";
import {
  hasResult,
  jobBarMeta,
  stageBackLabel,
  stagePath,
  toShellJob,
  type ShellJob,
} from "../lib/shell-job";
import { JobNotFoundScreen } from "../screens/JobNotFound";
import { JobLoadingScreen, JobUnavailableScreen } from "../screens/JobShellStates";

const SUBTABS = [
  { seg: "review", label: "검토 · 고치기" },
  { seg: "export", label: "내보내기" },
] as const;

/** 작업 셸. **프레임이 아니다** — 앱의 하단 탭바처럼 프레임들이 딛고 서는 바닥이라
 * 번호를 갖지 않는다(PRD 7.7.0).
 *
 * 서브탭은 둘이다. `이력`은 단계가 아니고, 셸 헤더 우측의 `이력 ›`으로 들어가 `‹ 검토 ·
 * 고치기`로 돌아온다 — 앱의 `A-12`(한 작업의 이력)가 홈에서 행을 눌러 들어가는 별도 화면인
 * 것과 같다. **웹만 이력을 탭으로 들고 있던 것을 맞춘 것이다**(정본 목업 `W-50`).
 *
 * **입구가 전부 여기로 떨어진다**. 표의 행 · `A-53` 의 QR(`/j/:jobId`). 작업은 언제나
 * **서버에 묻는다.** ⛔ 예시 작업 갈래를 걷었다 (2026-10-04 사용자 지시). 예시 id 를 가로채
 * 남의 상품명을 단 가짜 작업을 그리던 자리였다. 이제 그런 주소는 서버가 404 를 주고 아래
 * 「찾을 수 없습니다」가 선다.
 *
 * 불러오는 중·못 불러옴·없음은 **다른 화면**이다(`JobShellStates`). 뭉개면 열여덟 건을 가진
 * 셀러가 "없습니다"를 본다.
 *
 * ⚠️ PRD 7.7.0 은 아직 서브탭 셋(`이력` 포함)으로 적혀 있다. 목업이 나중이다. */
export function JobShell() {
  const { jobId } = useParams();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  // ⚠️ `enabled` 는 `lib/use-job-view.ts` 와 같은 조건(`Boolean(jobId)`)이어야 한다. 같은
  // 질의 키라 캐시가 한 벌이다.
  const query = useQuery({
    queryKey: ["job", jobId],
    queryFn: () => getJob(jobId as string),
    enabled: Boolean(jobId),
  });

  const job = useMemo<ShellJob | null>(
    () => (query.data ? toShellJob(query.data) : null),
    [query.data],
  );

  if (!jobId) return <Navigate to="/jobs" replace />;

  if (!job) {
    if (query.isPending) return <JobLoadingScreen />;
    // 404 만 "없다"다. 나머지는 우리 쪽이 답을 못 준 것이라 그렇게 말한다.
    const status = query.error instanceof ApiError ? query.error.status : null;
    if (status === 404) return <JobNotFoundScreen />;
    return <JobUnavailableScreen onRetry={() => void query.refetch()} />;
  }

  const segment = pathname.split("/")[3] ?? "";
  const resultMode = hasResult(job);
  const onHistory = segment === "history";

  // 결과가 없는 작업으로 결과 모드 주소를 열면 단계 화면으로 보낸다. 예전 링크를 열거나
  // 처리 중에 새로고침하는 경로가 실제로 있다(PRD 7.7.0).
  if (!resultMode && (segment === "review" || segment === "export")) {
    return <Navigate to={stagePath(job)} replace />;
  }

  // 우측 모노 줄. **정본은 PRD `7.7.0` 의 표이고 조립은 `jobBarMeta` 한 자리다.**
  // 셸 안에 삼항으로 두면 갈래가 늘 때 한쪽이 조용히 뭉개진다 — 실제로 그렇게 뭉개져
  // `W-31` 과 단계 모드 셋이 함께 어긋나 있었다.
  const meta = jobBarMeta(job, segment);

  // **`이력 ›` 은 판이 있을 때만 붙는다**(목업 실측. PRD `7.7.0`). 지금 이력 화면이면
  // 갈 곳이 자기 자신이라 빼고, 판이 없으면 열어도 볼 줄이 없다.
  //
  // ⛔ **`resultMode` 로 대신하지 마라.** 목업 여섯 자리에서는 두 조건이 같은 답을 내지만
  // 같은 규칙이 아니다. 판은 승인·내보내기에서만 생기므로 **결과는 있는데 판이 아직 없는
  // 작업**이 존재하고(`currentVersionNo === null`), 그때 `resultMode` 로 짜면 **모노 줄은
  // 판이 없다고 말하면서 판 목록으로 가는 문만 열린다.**
  const showHistoryLink = !onHistory && Boolean(job.versionLabel);

  // **서브탭은 `W-30`·`W-40` 둘뿐이다.** `W-31` 은 단계 모드라 서브탭 줄이 없다
  // (정본 목업 4093행: `jobbar` 다음이 `web-tabs` 가 아니라 바로 `web-panel` 이다).
  // ⚠️ **상태가 아니라 주소가 가른다** — 같은 `준비됨` 작업이 `W-31` 과 `W-30` 둘 다에
  // 설 수 있어서 `resultMode` 로 짜면 결과 확인 화면에 탭 줄이 생긴다.
  const showSubtabs = segment === "review" || segment === "export";

  return (
    <div className="wrap">
      <div className="shell">
        <AppBar />

        <div className="jobbar">
          {/* 목록으로 돌아가는 길이 헤더 안에 없으면 셀러는 방금 열었던 작업을 잃는다. */}
          {/* 돌아갈 곳의 이름을 그대로 쓴다. 이력에서는 왔던 화면으로, 그 밖에서는 목록으로. */}
          <Link to={onHistory ? stagePath(job) : "/jobs"} className="btn ghost small">
            {/* **낱말은 정본 목업이 정한다** — 다섯 자리가 `‹ 목록`, `W-50` 은 **돌아갈 화면의
                이름**이다(PRD `7.5.1a` 「`W-50` 작업 바의 돌아가는 길」). 글자는 `stagePath` 와
                같은 표(`stageBackLabel`)에서 온다. 옛 `‹ 이 작업으로` 는 코드가 지어낸 말이었고,
                옛 삼항은 `READY` 에 `‹ 검토 · 고치기` 를 적으면서 `W-31` 결과 확인을 열었다.
                옛 `‹ 내 작업` 은 **없어진 상단 탭의 이름**이었다. */}
            {onHistory ? stageBackLabel(job) : "‹ 목록"}
          </Link>
          <span className="jname">
            <CheckerThumb size={34} sample={job.obj} />
            <span className="paneltitle">{job.name}</span>
          </span>
          {/* ⛔ **작업 바에 상태 칩을 두지 않는다**(정본 목업 여섯 자리 전부. `jobbar` 의
              자식은 뒤로 버튼 · 이름 · 모노 줄 셋뿐이다). 옛 판의 `좌 끝 상태 칩` 은 이미
              폐기됐고 그 잔재가 여기 남아 있었다. 칩을 두면 **한 줄이 같은 사실을 두 번
              적는다** — 바로 옆 모노 줄이 이미 상태를 말한다(PRD `7.7.0`). 칩이 서는 자리는
              `W-20` 의 표와 앱 목록이다. */}
          <span className="jobmeta">
            {/* 없는 값을 줄표로 채우지 않는다. 자리를 남기면 셀러는 뭔가 빠진 줄 읽는다. */}
            <span className="mono">{meta}</span>
            {showHistoryLink ? (
              <Link className="histlink" to={`/jobs/${job.id}/history`}>
                이력{" "}
                <span className="chev" aria-hidden="true">
                  ›
                </span>
              </Link>
            ) : null}
          </span>
        </div>

        {showSubtabs ? (
          <div className="web-tabs" role="tablist" aria-label="이 작업 안에서">
            {SUBTABS.map((tab) => {
              const selected = segment === tab.seg;
              return (
                <button
                  key={tab.seg}
                  type="button"
                  className="web-tab"
                  role="tab"
                  aria-selected={selected}
                  tabIndex={selected ? 0 : -1}
                  onClick={() => navigate(`/jobs/${job.id}/${tab.seg}`)}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>
        ) : null}

        <div className="panel">
          <Outlet context={job} />
        </div>
      </div>
    </div>
  );
}
